import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { PeriodType, ClassType } from '../../../api';
import { Player } from '../../../services/references';
import { LineupReason } from '../../../services/sessionsUpload';
import { TLineupEntry } from './SessionLineupsPlayer';
import SelectSinglePlayer from '../../Util/SelectSinglePlayer';
import SelectSinglePeriod from '../../Util/SelectSinglePeriod';
import styled from 'styled-components';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import StopIcon from '@material-ui/icons/Stop';
import InjuryIcon from '@material-ui/icons/LocalHospitalOutlined';

interface Props {
  open: boolean;
  onClose: () => void;
  lineupEntry: TLineupEntry;
  team?: string;
  allPlayers: Array<string>;
  periods: Array<PeriodType>;
  onAddExit: (out: TLineupEntry, reason: LineupReason, outSeconds: number, period: PeriodType) => void;
  onAddSubstitution: (out: TLineupEntry, outSeconds: number, period: PeriodType, playerIn: Player) => void;
}

const ExitDialog: React.FunctionComponent<Props> = ({
  open,
  onClose,
  onAddSubstitution,
  onAddExit,
  lineupEntry,
  team,
  allPlayers,
  periods,
}) => {
  const [reason, setReason] = useState<LineupReason>(LineupReason.Substitution);
  const [outMinute, setOutMinute] = useState(Math.ceil(lineupEntry.inSeconds / 60) + '');
  const [period, setPeriod] = useState<PeriodType>();
  const [playerIn, setPlayerIn] = useState<Player | null>();
  const onOk = useCallback(() => {
    if (period) {
      if (reason === LineupReason.Substitution && playerIn && parseInt(outMinute) > 0) {
        onAddSubstitution(lineupEntry, parseInt(outMinute) * 60, period, playerIn);
        onClose();
      } else if (
        (reason === LineupReason.Injury || reason === LineupReason.RedCard) &&
        parseInt(outMinute) > 0
      ) {
        onAddExit(lineupEntry, reason, parseInt(outMinute) * 60, period);
        onClose();
      }
    }
  }, [lineupEntry, onAddExit, onAddSubstitution, onClose, outMinute, period, playerIn, reason]);

  const onChangeReason = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setReason((event.target as HTMLInputElement).value as LineupReason);
  }, []);

  const onChangeOutMinute = useCallback((event) => {
    const isNum = /^\d+$/.test(event.target.value);
    const val = parseInt(event.target.value);
    if ((isNum && val >= 0 && val < 200) || event.target.value === '') setOutMinute(event.target.value);
  }, []);

  const onChangePeriod = useCallback((newperiod) => {
    setPeriod(newperiod);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">Leave pitch</DialogTitle>
      <DialogContent>
        <PlayerWrapper>
          <TextField value={lineupEntry.player?.name || ''} disabled label="Player leaving" />
        </PlayerWrapper>
        <MinuteWrapper>
          <TextField
            value={outMinute}
            InputProps={{ inputProps: { min: Math.ceil(lineupEntry.inSeconds / 60) } }}
            onChange={onChangeOutMinute}
            type="number"
            label="Minute"
          />
        </MinuteWrapper>
        <PeriodWrapper>
          <SelectSinglePeriod value={period} onChange={onChangePeriod} periods={periods} />
        </PeriodWrapper>
        <ReasonWrapper>
          <FormControl component="fieldset">
            <FormLabel component="legend">Reason</FormLabel>
            <RadioGroup row aria-label="position" name="position" value={reason} onChange={onChangeReason}>
              <FormControlLabel
                value={LineupReason.Substitution}
                control={<Radio color="primary" />}
                label={
                  <LabelContainer>
                    Substitution
                    <ArrowDropUpIcon fontSize="large" htmlColor="#ff0000" />
                  </LabelContainer>
                }
                labelPlacement="end"
              />
              <FormControlLabel
                value={LineupReason.RedCard}
                control={<Radio color="primary" />}
                label={
                  <LabelContainer>
                    Red card
                    <StopIcon fontSize="large" htmlColor="#ff0000" />
                  </LabelContainer>
                }
                labelPlacement="end"
              />
              <FormControlLabel
                value={LineupReason.Injury}
                control={<Radio color="primary" />}
                label={
                  <LabelContainer>
                    Permanent injury
                    <InjuryIcon color="disabled" />
                  </LabelContainer>
                }
                labelPlacement="end"
              />
            </RadioGroup>
          </FormControl>
        </ReasonWrapper>
        {reason === LineupReason.Substitution && (
          <SubstitutionWrapper>
            <SelectSinglePlayer
              keeper={lineupEntry.type === ClassType.AwayKeeper || lineupEntry.type === ClassType.HomeKeeper}
              add
              exclude={allPlayers}
              team={team}
              value={playerIn?.id || null}
              onChange={setPlayerIn}
            />
          </SubstitutionWrapper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button color="primary" autoFocus onClick={onOk}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const PeriodWrapper = styled.div`
  margin-top: 16px;
`;
const PlayerWrapper = styled.div`
  margin-top: 16px;
`;
const MinuteWrapper = styled.div`
  margin-top: 16px;
`;
const ReasonWrapper = styled.div`
  margin-top: 16px;
`;
const SubstitutionWrapper = styled.div`
  margin-top: 16px;
`;
export default ExitDialog;
